<!--
  分享紀錄的組織圖（手機優先）。

  由左往右長：同一層的人上下排、往下一層往右。手機是直的，上下排比左右排放得下多很多人；
  以前的 ECharts 圖是由上往下，人一多就變得很寬，手機上只能縮到看不清楚再拖來拖去。

  - 純 HTML／CSS 畫卡片和連接線，用原生捲動拖曳（手機上手指滑就好），字不會因為縮放糊掉
  - 點有下線的卡片可以收合／展開；人數少（30 人以內）一開始全部展開，多的話只先展開第一層
  - 搜尋時全部展開，比對到的人標黃
-->
<template>
  <div class="org-chart">
    <div ref="viewport" class="org-chart__viewport">
      <div class="org-chart__canvas" :style="{ zoom }">
        <OrgChartNode :node="rootNode" :depth="-1" />
      </div>
    </div>

    <div class="org-chart__zoom">
      <v-btn icon small aria-label="縮小" :disabled="zoomIndex === 0" @click="zoomIndex--">
        <v-icon size="20">mdi-minus</v-icon>
      </v-btn>
      <button type="button" class="org-chart__zoom-label" aria-label="恢復原尺寸" @click="zoomIndex = defaultZoomIndex">{{ Math.round(zoom * 100) }}%</button>
      <v-btn icon small aria-label="放大" :disabled="zoomIndex === zoomLevels.length - 1" @click="zoomIndex++">
        <v-icon size="20">mdi-plus</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import OrgChartNode from "./OrgChartNode.vue";
import { buildOrgTree, matchesQuery } from "./orgUtils";

// 人數在這以內一開始就全部展開；超過的話只看得到直屬，要看誰底下的再點開
const EXPAND_ALL_LIMIT = 30;

export default {
  name: "OrgChart",
  components: { OrgChartNode },
  // 收合狀態放在這裡統一管，節點用 inject 讀寫（遞迴元件一層層傳 props 太囉唆）
  provide() {
    return { orgChart: this };
  },
  props: {
    // 平面資料，搜尋過濾（含上層／下層）由 ShareOrgDialog 做好才傳進來
    referrals: { type: Array, default: () => [] },
    searchQ: { type: String, default: "" },
    // 最左邊那張「我」的卡片
    root: { type: Object, default: () => ({ name: "", grade: "" }) },
  },
  data() {
    return {
      // guid → 使用者手動展開(true)／收合(false)；沒有的就照預設
      expanded: {},
      zoomLevels: [0.6, 0.75, 0.9, 1, 1.15],
      defaultZoomIndex: 3,
      zoomIndex: 3,
    };
  },
  computed: {
    q() {
      return (this.searchQ || "").trim();
    },
    zoom() {
      return this.zoomLevels[this.zoomIndex];
    },
    totalCount() {
      return (this.referrals || []).length;
    },
    rootNode() {
      return {
        guid: "__me__",
        name: (this.root?.name || "").trim() || "我",
        grade: this.root?.grade || "",
        isRoot: true,
        children: buildOrgTree(this.referrals || []),
      };
    },
  },
  watch: {
    // 換搜尋字就回到預設（搜尋中全部展開），之前手動收合的不留
    q() {
      this.expanded = {};
    },
  },
  methods: {
    isOpen(node, depth) {
      if (node.isRoot) return true;
      if (this.expanded[node.guid] !== undefined) return this.expanded[node.guid];
      if (this.q) return true;
      return this.totalCount <= EXPAND_ALL_LIMIT || depth < 0;
    },
    toggle(node, depth) {
      if (node.isRoot) return;
      this.$set(this.expanded, node.guid, !this.isOpen(node, depth));
    },
    isHit(node) {
      return !node.isRoot && matchesQuery(node, this.q);
    },
    /** 父層在 dialog 打開或切回組織圖時呼叫（以前的 ECharts 版要重算尺寸，這版不用，留著相容） */
    resize() {},
  },
};
</script>

<style scoped>
.org-chart {
  position: relative;
  width: 100%;
  height: 100%;
  background: #f7f7fb;
}

/* 用原生捲動：手機上直接用手指拖 */
.org-chart__viewport {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px 16px 72px;
}

.org-chart__canvas {
  display: inline-block;
  min-width: 100%;
}

/* 縮放鈕：浮在圖的右下角 */
.org-chart__zoom {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.org-chart__zoom-label {
  min-width: 48px;
  border: 0;
  background: none;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
}
</style>
